'use client';

import React, { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';
import type { z } from 'zod';
import styles from '../LoginForm/LoginForm.module.css';
import { requestMagicLinkSchema } from './RequestMagicLinkForm.schema';
import type { FeedbackMessageBannerProps } from '../FeedbackMessageBanner/FeedbackMessageBanner.types';
import { AuthenticationIntro } from '../AuthenticationIntro/AuthenticationIntro';
import { Button } from '../../components/Button/Button';
import { Input } from '../../components/Input/Input';

export type RequestMagicLinkFormValues = z.infer<typeof requestMagicLinkSchema>;

export type RequestMagicLinkFormProps = Readonly<{
  onSubmit: (data: RequestMagicLinkFormValues) => void | Promise<void>;
  banner?: Pick<FeedbackMessageBannerProps, 'variant' | 'message'>;
}>;

export function RequestMagicLinkForm({ onSubmit, banner }: RequestMagicLinkFormProps) {
  const { control, handleSubmit } = useForm<RequestMagicLinkFormValues>({
    resolver: zodResolver(requestMagicLinkSchema),
    defaultValues: { email: '' },
  });

  const defaultBanner = useMemo<Pick<FeedbackMessageBannerProps, 'variant' | 'message'>>(
    () => ({
      variant: 'info',
      message: "Enter your email address and we'll send you a link that'll sign you in instantly.",
    }),
    []
  );
  const effectiveBanner = banner ?? defaultBanner;

  return (
    <div className="bg-white p-lr-x3 p-tb-1-75 rounded-3xl w-full grid justify-items-center text-center gap-6">
      <AuthenticationIntro
        title="Send Magic Link"
        subtitle="To Sign In Instantly"
        banner={{ message: effectiveBanner.message, variant: effectiveBanner.variant }}
      />

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="grid gap-6 w-full text-left">
        <Controller
          control={control}
          name="email"
          render={({ field, fieldState: { error } }) => (
            <Input
              label="Email *"
              placeholder="eg. user@domain.com"
              className={styles.input}
              leftIcon={<i className="clara-envelope-outline" aria-hidden="true" />}
              type="email"
              field={field}
              validationMessage={error?.message}
            />
          )}
        />

        <Button className={`${styles.signInButton} justify-self-center`} type="submit">
          Send Magic Link
        </Button>
      </form>
      <Link href="/login" className="w-full flex justify-center p-tb-1-5" aria-label="Back to sign in">
        <Button styleType="outline" className={`mb-[var(--h1-5)] ${styles.backButton}`} variant="primary" aria-label="Back to sign in">
          <i className="clara-chevron-left-outline" aria-hidden="true" />
          Back To Sign In
        </Button>
      </Link>
    </div>
  );
}
